import type { ContentAsset } from './content-loading';
import type { EntityInvokeOutcome } from './entity-description';
import type { FileItem } from './file-value';
import type { ObjectContext } from './object-access';
import type { WidgetActionExecutionReceipt } from './widget-activity';
import type {
  WidgetStateReceipt,
  WidgetStateWriteOptions,
} from './widget-authoring';

/** Public author ABI. Compiler implementation is intentionally a separate entry. */
export const WIDGET_RUNTIME_ABI = 1;
export const WIDGET_COMPILER_VERSION = '2.1.0';
export const WIDGET_SOURCE_MAX_BYTES = 1024 * 1024;
export const WIDGET_LOCAL_MAX_BYTES = 1024 * 1024;
export const WIDGET_LOCAL_MAX_CONCURRENCY = 8;
export const WIDGET_LOCAL_MAX_MODULES = 64;

export type WidgetDiagnostic = Readonly<{
  code: string;
  message: string;
  line?: number;
  column?: number;
}>;
export type WidgetLocalReference<Input = unknown, Output = unknown> = Readonly<{
  __xuwoLocal: 1;
  name: string;
  /** Phantom fields; never present on the wire. */
  __input?: Input;
  __output?: Output;
}>;
export type WidgetCompiledRuntime = Readonly<{
  apiVersion: 1;
  localCode: string | null;
  functions: readonly string[];
  nodeBuiltins: readonly string[];
  containsLocalCode: boolean;
}>;
export type WidgetCompilation =
  | Readonly<{ code: string; error: null; runtime?: WidgetCompiledRuntime }>
  | Readonly<{ code: null; error: WidgetDiagnostic; runtime?: never }>;

/** These references identify definitions, never physical processes. */
export type WidgetDefinitionAddress =
  | Readonly<{ kind: 'inline'; docId: string; blockId: string }>
  | Readonly<{ kind: 'library'; libraryId: string }>
  | Readonly<{ kind: 'package'; packageId: string; widgetId: string }>;
export function widgetDefinitionKey(address: WidgetDefinitionAddress): string {
  switch (address.kind) {
    case 'inline':
      return JSON.stringify(['inline', address.docId, address.blockId]);
    case 'library':
      return JSON.stringify(['library', address.libraryId]);
    case 'package':
      return JSON.stringify(['package', address.packageId, address.widgetId]);
  }
}
export type WidgetRuntimeStatus = Readonly<{
  trusted: boolean;
  disabledReason?: 'WIDGET_RUNTIME_RECOVERY_MODE';
  generation: number;
  digest: string;
  containsLocalCode: boolean;
  functions: readonly string[];
}>;
export type WidgetLocalContext = Readonly<{
  workspaceId: string;
  definitionId: string;
  instanceId: string;
  mountId: string;
  docId: string;
  blockId: string;
}>;
/** Bounded operational metadata only: never arguments, return values, paths or raw errors. */
export type WidgetLocalCallDiagnostic = Readonly<{
  id: string;
  implementationId: string;
  definitionId: string;
  instanceId: string;
  mountId: string;
  functionName: string;
  startedAt: number;
  elapsedMs: number;
  timedOut: boolean;
  outcome: 'running' | 'succeeded' | 'failed' | 'unknown';
  code?: string;
}>;
export type WidgetLocalHostDiagnostics = Readonly<{
  running: boolean;
  stopping: boolean;
  calls: number;
  modules: number;
  pending: readonly WidgetLocalCallDiagnostic[];
  recent: readonly WidgetLocalCallDiagnostic[];
}>;

/**
 * 小组件实例上的一个呈现交互(切 tab、展开、筛选……),经 `xuwo.ui.expose` 声明,
 * 人点和 AI 的 `widget_control` 走同一个处理。业务不走这里:按钮做业务调实体动作
 * 或方法(16c)。
 */
export type WidgetDeclaredAction = Readonly<{
  name: string;
  summary: string;
  /** 参数的 JSON Schema(宿主已实现的子集)。 */
  params?: Readonly<Record<string, unknown>>;
  handler: (params: unknown) => unknown | Promise<unknown>;
}>;

/** 宿主推给小组件的主题快照:明暗与读到值的 `--xuwo-*` token(不带前缀)。 */
export type WidgetTheme = Readonly<{
  mode: 'light' | 'dark';
  tokens: Readonly<Record<string, string>>;
}>;

/** `xuwo.ui.menu` 的一项;`items` 是子菜单,`group: true` 时画成分组。 */
export type WidgetMenuItem = Readonly<{
  /** 选中时回的值;缺省用 `name`。 */
  id?: string;
  name: string;
  /**
   * 图标:一个 emoji 或 `lucide:<名>`(与清单图标同一条规则,`displayIconSchema`)。
   * 旧的宿主图标库名(如 `trash`)按固定对照表翻成最接近的 Lucide 图标;认不出的
   * 当没给,不报错。
   */
  icon?: string;
  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** 行尾提示(禁用原因、当前取值等);有它时不画选中的勾。 */
  hint?: string;
  /** 悬停看的说明(行里放不下的解释)。 */
  tooltip?: string;
  /** 名字下面一行小字(这一项是什么、多少钱之类);长了截断。 */
  description?: string;
  group?: boolean;
  items?: readonly WidgetMenuItem[];
}>;

/** 小组件自己坐标(或视口坐标)里的矩形,弹层锚在它上面。 */
export type WidgetRect = Readonly<{
  x: number;
  y: number;
  width: number;
  height: number;
}>;

export type WidgetMenuInput = Readonly<{
  items: readonly WidgetMenuItem[];
  rect: WidgetRect;
  /** 缺省 `widget`:`rect` 是小组件自己的坐标。 */
  coordinateSpace?: 'widget' | 'viewport';
  title?: string;
  search?: boolean;
  width?: number;
}>;

/** `xuwo.assets.packageUrl` 的参数:哪个包的哪项随包资源。 */
export type WidgetPackageAssetInput = Readonly<{
  packageId: string;
  assetId: string;
  packageVersion?: string;
}>;
export type WidgetPackageAsset = Readonly<{
  url: string;
  mediaType: string;
  bytes: number;
}>;

/**
 * `xuwo.ai` 的一段消息内容:文字,或内联图片(`data:<mime>;base64,…`,不取远程地址;
 * 只放在 user 消息里,模型要支持看图)。
 */
export type WidgetAiContentPart =
  | Readonly<{ type: 'text'; text: string }>
  | Readonly<{ type: 'image'; url: string }>;
/**
 * 一条对话消息。assistant 消息可带上一轮模型发起的 `toolCalls`(原样回放);`tool` 消息
 * 回填某次工具调用的结果,`toolCallId` 对上前面 assistant 消息里的一个 `id`。
 */
export type WidgetAiMessage =
  | Readonly<{
      role: 'system' | 'user';
      content: string | readonly WidgetAiContentPart[];
    }>
  | Readonly<{
      role: 'assistant';
      content: string | readonly WidgetAiContentPart[];
      toolCalls?: readonly WidgetAiToolCall[];
      /**
       * 这一轮模型的思考(上次结果里的 `reasoning`)。同一件事接着做(工具往返、修错)时原样带回,
       * 模型不丢掉自己的推理,服务端的前缀缓存也接得上;OpenAI 兼容的中转按 `reasoning_content` 转发,
       * 其他协议丢掉。跨轮次的历史一般不带。
       */
      reasoning?: string;
    }>
  | Readonly<{ role: 'tool'; toolCallId: string; content: string }>;
/**
 * 给模型的一个工具(函数)。`name` 为 1–64 个字母、数字、`_` 或 `-`,一次请求里不重名;
 * `parameters` 是参数的 JSON Schema,顶层 `type: 'object'`。
 */
export type WidgetAiTool = Readonly<{
  name: string;
  description?: string;
  parameters: Readonly<Record<string, unknown>>;
}>;
/**
 * 模型发起的一次工具调用。`arguments` 是模型写的 JSON 文本(应是一个对象,但模型可能
 * 写坏,解析前自己兜);流式时由宿主拼完整后随结果一次给出。
 */
export type WidgetAiToolCall = Readonly<{
  id: string;
  name: string;
  arguments: string;
}>;
/**
 * `auto` 模型自己决定;`none` 不调工具;`required` 必须调至少一个;`{ name }` 必须调这个。
 * 后两种有的协议表达不了(如 Google 协议不能指定某个工具),这时抛
 * `WIDGET_AI_UNSUPPORTED_PARAMETER`,不降级成 `auto`。
 */
export type WidgetAiToolChoice =
  | 'auto'
  | 'none'
  | 'required'
  | Readonly<{ name: string }>;
/**
 * `xuwo.ai.chat` 的请求。`model` 取 `xuwo.ai.models()` 里的 id,缺省 `default`
 * (跟随应用默认模型);没给的参数不替你填,system 消息原样作系统提示。
 */
export type WidgetAiChatRequest = Readonly<{
  model?: string;
  messages: readonly WidgetAiMessage[];
  temperature?: number;
  topP?: number;
  maxTokens?: number;
  stop?: string | readonly string[];
  /** 原生工具调用:至多 64 个,序列化后合计至多 128 KiB。 */
  tools?: readonly WidgetAiTool[];
  /** 缺省交服务商默认(有工具时一般等于 `auto`);要给 `tools` 才能给它。 */
  toolChoice?: WidgetAiToolChoice;
  /**
   * 提示缓存:同一个 `key` 的调用共用服务商的缓存前缀(长系统提示、角色卡、世界设定
   * 放在消息最前面且保持不变,命中才多)。`key` 由宿主按「工作区 × 小组件定义」隔开,
   * 不同小组件取同名 key 不会互相命中;`retention` 是 `short`(服务商默认,几分钟)
   * 或 `long`(支持的服务商保留更久,如 OpenAI 24 小时、Anthropic 1 小时)。
   */
  cache?: WidgetAiCacheOptions;
  /**
   * 思考强度:`off` 关思考,其余照 OpenAI 的 `reasoning_effort`;不给就交服务商默认。
   * 模型没有思考档位(`capabilities` 不含 `reasoning`)时多半被上游忽略。
   */
  reasoning?: WidgetAiReasoning;
}>;
export type WidgetAiReasoning =
  | 'off'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh';
export type WidgetAiCacheOptions = Readonly<{
  key: string;
  retention?: 'short' | 'long';
}>;
export type WidgetAiChatOptions = Readonly<{
  /** 流式:每来一段新文字回调一次(只含新增部分)。 */
  onDelta?: (text: string) => void;
  /** 流式收思考:模型在正文之前(或之间)想的内容,每来一段回调一次。 */
  onReasoning?: (text: string) => void;
  /** 中止即取消;小组件卸载或关闭时在途调用也自动取消。 */
  signal?: AbortSignal;
}>;
/**
 * 上游报告的用量;`inputTokens` 含缓存读写。上游没报就没有这一项;缓存读写只在
 * 服务商报了才有(命中缓存的部分按缓存价计)。
 */
export type WidgetAiUsage = Readonly<{
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  reasoningTokens?: number;
  /** 估算金额(美元),与应用用量页同一套价格(可在设置里改);价格未知就没有。 */
  costUsd?: number;
}>;
/** 宿主推回的一段流式增量:正文或思考。 */
export type WidgetAiDelta = Readonly<{ text?: string; reasoning?: string }>;
export type WidgetAiChatResult = Readonly<{
  /** 完整回复文字(等于各次 `onDelta` 拼起来)。 */
  text: string;
  /** 模型这次的思考全文(等于各次 `onReasoning` 拼起来);上游没给就没有。 */
  reasoning?: string;
  /**
   * `stop` 正常结束,`length` 到了 `maxTokens`,`tool_calls` 模型要调工具;其余照上游原样。
   */
  finishReason: string;
  /** 模型这一轮发起的工具调用(按上游顺序);没有就没有这一项。 */
  toolCalls?: readonly WidgetAiToolCall[];
  usage?: WidgetAiUsage;
  /** 实际用到的模型 id(`default` 解析成的那个 `<连接>/<模型>`)。 */
  model: string;
}>;
/** 模型属于哪条连接:`slug` 是 id 的前缀,`name` 是用户在设置里起的名字(界面上显示它)。 */
export type WidgetAiModelConnection = Readonly<{ slug: string; name: string }>;
/** 模型能力:`vision` 能看图,`reasoning` 有思考档位;没声明、目录也不知道的不出现。 */
export type WidgetAiModelCapability = 'vision' | 'reasoning';
export type WidgetAiModelList = Readonly<{
  /**
   * 可用模型;有默认模型时 `default` 在最前(它没有 `connection`)。`model` 是连接里的
   * 模型名(id 去掉 `<连接>/` 前缀)。
   */
  models: readonly Readonly<{
    id: string;
    contextWindow?: number;
    connection?: WidgetAiModelConnection;
    model?: string;
    capabilities?: readonly WidgetAiModelCapability[];
  }>[];
  /** `default` 此刻指向的模型 id;没有可用默认时为 null。 */
  defaultModel: string | null;
  /** 能生图的模型(`xuwo.ai.image` 的 `model`);没有能生图的连接时为空。 */
  imageModels: readonly Readonly<{
    id: string;
    connection?: WidgetAiModelConnection;
    model?: string;
    /** 接受参考图(能改图、保持人物一致);不接受的模型收到 `images` 会报 `WIDGET_AI_UNSUPPORTED_PARAMETER`。 */
    referenceImages?: boolean;
  }>[];
  /** `xuwo.ai.image` 不给 `model` 时用的那个;没有时为 null。 */
  defaultImageModel: string | null;
  /**
   * 此刻不提供的连接及原因(没登录或没存 Key;以个人订阅登录的服务商只有 ChatGPT
   * 订阅提供给小组件,Claude 订阅不提供)。
   */
  excluded: readonly Readonly<{
    slug: string;
    name: string;
    reason:
      | 'subscription_connection_not_offered'
      | 'connection_not_authenticated';
  }>[];
}>;
/**
 * `xuwo.ai.pickModel` 的输入:在 `rect` 处弹应用的模型选择器(按连接分组、可搜索、
 * 行尾标能力与上下文窗口,与 XuWo AI 聊天里的选择器同一个)。
 */
export type WidgetAiPickModelInput = Readonly<{
  rect: WidgetRect;
  /** 缺省 `widget`:`rect` 是小组件自己的坐标。 */
  coordinateSpace?: 'widget' | 'viewport';
  /** 当前存的模型 id;空串或缺省表示「默认」。 */
  current?: string;
  /** `chat`(缺省)列对话模型,`image` 只列能生图的。 */
  kind?: 'chat' | 'image';
  /**
   * 与 `kind: 'image'` 一起用:只列接受参考图的生图模型(调用会带 `images` 时);默认生图模型
   * 不接受参考图时也不出「默认」行。
   */
  referenceImages?: boolean;
}>;
/**
 * `xuwo.ai.image` 的请求。`images` 是参考图(编辑一张图、保持人物或画风一致):
 * 内联 `data:image/…;base64,…`、本工作区文件的地址(`files:file:…`),
 * 或文件属性的一项(`xuwo.ai.image` 返回的 `image`、文件格里的项)。尺寸、质量与背景
 * 没给就交服务商默认。
 */
export type WidgetAiImageRequest = Readonly<{
  prompt: string;
  /** `xuwo.ai.models().imageModels` 里的 id;缺省 `defaultImageModel`。 */
  model?: string;
  /** `auto` 或 `<宽>x<高>`,如 `1024x1536`。 */
  size?: string;
  quality?: 'low' | 'medium' | 'high' | 'auto';
  background?: 'transparent' | 'opaque' | 'auto';
  images?: readonly (string | FileItem)[];
}>;
export type WidgetAiImageOptions = Readonly<{
  /** 中止即取消;小组件卸载或关闭时在途调用也自动取消。 */
  signal?: AbortSignal;
}>;
export type WidgetAiImageResult = Readonly<{
  /**
   * 生成的图,已存进本工作区的文件:文件属性的一项(可直接放进文件属性的值),
   * 交给 `xuwo.files.url(image)` 就能显示。
   */
  image: FileItem & Readonly<{ key: string; size: number }>;
  /** 服务商改写后实际用来画的提示;没报就没有。 */
  revisedPrompt?: string;
  /** 实际用到的 `<连接>/<模型>`。 */
  model: string;
  /** 输入含文字与参考图;上游没报就没有。 */
  usage?: Readonly<{ inputTokens: number; outputTokens: number }>;
}>;
/** `xuwo.ai` 失败时 `WidgetRuntimeError.code` 的取值。 */
export type WidgetAiErrorCode =
  /** 此宿主未连接可用的桌面网关或官方云模型。 */
  | 'WIDGET_AI_UNAVAILABLE'
  | 'WIDGET_AI_CANCELLED'
  | 'WIDGET_AI_INVALID_REQUEST'
  /** 这个模型的协议表达不了的参数或内容(如远程图片地址)。 */
  | 'WIDGET_AI_UNSUPPORTED_PARAMETER'
  | 'WIDGET_AI_MODEL_NOT_FOUND'
  | 'WIDGET_AI_NO_DEFAULT_MODEL'
  /** 没有哪条连接能生图(`xuwo.ai.image`)。 */
  | 'WIDGET_AI_NO_IMAGE_MODEL'
  /** 服务商的内容审核拒绝了这次生图;`message` 是服务商原话。 */
  | 'WIDGET_AI_CONTENT_POLICY_REFUSED'
  /** 服务商报额度或频率用尽。 */
  | 'WIDGET_AI_QUOTA_EXCEEDED'
  | 'WIDGET_AI_SUBSCRIPTION_CONNECTION_NOT_OFFERED'
  | 'WIDGET_AI_CONNECTION_NOT_AUTHENTICATED'
  | 'WIDGET_AI_UPSTREAM_ERROR'
  | 'WIDGET_AI_UPSTREAM_UNREACHABLE'
  | 'WIDGET_AI_FAILED';

export type WidgetPageIdentity = Readonly<{
  id: string;
  title?: string;
  type?: string;
}>;
export type WidgetPageProperty = Readonly<{
  id: string;
  name: string;
  type: string | null;
  value: unknown;
}>;
/** `content.add` 的一项:`widget` 是随包组件 `<包 id>/<组件 id>`。 */
export type WidgetChildInput = Readonly<{
  widget: string;
  title?: string;
  state?: unknown;
  span?: number;
  rows?: number;
}>;
/**
 * `content.arrange` 的一项:按给出的顺序排在网格末尾;`widget` 换成同数据的另一个
 * 组件,`span` / `rows` 是占格,`size.min` 是这块的最小内容视口(CSS px,与声明
 * `size.min` 同形)。
 */
export type WidgetArrangeInput = Readonly<{
  blockId: string;
  widget?: string;
  span?: number;
  rows?: number;
  size?: Readonly<{
    min?: Readonly<{ width?: number; height?: number }>;
  }>;
}>;
/** 切换容器的一面:`key` 稳定(当前面存的就是它),`name` 是给人看的名字。 */
export type WidgetSwitchFace = Readonly<{ key: string; name: string }>;
/**
 * 这一页上一个切换容器(`xuwo.switcher.containers` 的一项)。`relation` 说明它和
 * 这块小组件的关系:`container` 包住它,`nearby` 在它的同级(逐层往上看),
 * `page` 是页面上其余的;缺省目标排第一。`current` 是此刻显示的那一面的 key。
 */
export type WidgetSwitchContainer = Readonly<{
  id: string;
  relation: 'container' | 'nearby' | 'page';
  faces: readonly WidgetSwitchFace[];
  current: string | null;
}>;
export type WidgetContentSnapshot = readonly Readonly<{
  blockId: string;
  widget: string;
  title: string;
  state: unknown;
}>[];

/**
 * 小组件客户端合同(apiVersion 1)。成员缺省 stable;形状可能在下一个小版本调整的
 * 成员在文档注释里写 `@experimental`(与 SDK 的 `types/widget-*.d.ts` 同一写法,
 * 生成的接口参考据此计数),同一份标记的运行时读法是下面的
 * `WIDGET_CLIENT_EXPERIMENTAL_MEMBERS`(`widget-runtime.spec.ts` 核对二者一致)。
 */
export interface WidgetClient {
  /** Fixed by the host that owns this mount, never inferred from the frontmost window. */
  readonly context: WidgetLocalContext;
  /** @experimental */
  readonly expanded: boolean;
  readonly inContainer: boolean;
  /** @experimental */
  readonly reducedMotion: boolean;
  readonly content: {
    readonly available: boolean;
    readonly items: WidgetContentSnapshot;
    /**
     * 这一格正文内容(组)里顶层块的数量;没有内容是 0。
     * @experimental
     */
    readonly blocks: number;
    /**
     * 整组替换:把这一格的正文内容整体换成这份 Markdown(标题、公式、图片、
     * 表格都是真实文档块,用户之后可以照常编辑)。一次可撤销的编辑;实体样式里的
     * 小组件写的是当前实体自己的页面,不是共享样式。
     * @experimental
     */
    replace(markdown: string): Promise<Readonly<{ blocks: number }>>;
    add(widgets: readonly WidgetChildInput[]): Promise<readonly string[]>;
    addEmpty(): Promise<void>;
    /** @experimental */
    arrange(layout: readonly WidgetArrangeInput[]): Promise<void>;
    /**
     * 整组换成一篇文档的同步嵌入(`docId` 为 null 时撤掉):`editable` 让它在页里就地
     * 编辑,`borderless` 去掉标题栏与边框。已经嵌着一篇时原地改指向,指向与开关都
     * 没变不写。能不能写字只看被嵌文档自己,不看这一页锁没锁。
     * @experimental
     */
    embed(
      docId: string | null,
      options?: Readonly<{ editable?: boolean; borderless?: boolean }>
    ): Promise<void>;
  };
  /**
   * 切换容器(D747):同一位置几组内容一次显示一组,用什么切换(按钮、标签、
   * 图片……)由组件自己画。缺省作用于包住这块小组件的最近一层切换容器;不在
   * 任何切换容器里时是同级最近的那一个,再没有就是页面上第一个。`containers`
   * 列出这一页全部切换容器(缺省目标排第一),`show` 带 `{ container }` 切别的。
   * 当前面是呈现状态:不写文档、不进版本历史,可恢复,所以直接执行;页面锁(含
   * 出厂锁定页)下照常能切。每个视图各看各的面(同一页开在两个窗口互不改写),
   * 上一次的选择按页面记住,下次打开从它开始;AI 经 MCP `document_switch` 切换时
   * 已打开的视图都跟上。变化时组件随之重渲染。
   * @experimental
   */
  readonly switcher: {
    /** 这一页有没有能控制的切换容器。 */
    readonly available: boolean;
    /** 缺省目标的各面(按顺序);没有目标时为空。 */
    readonly faces: readonly WidgetSwitchFace[];
    /** 缺省目标此刻显示的那一面的 key;没有目标时为 null。 */
    readonly current: string | null;
    readonly containers: readonly WidgetSwitchContainer[];
    /**
     * 切到某一面。没有这一面回 `WIDGET_SWITCH_FACE_NOT_FOUND`,页面上没有切换
     * 容器回 `WIDGET_SWITCH_UNAVAILABLE`,`container` 不在这一页回
     * `WIDGET_SWITCH_NOT_FOUND`。
     */
    show(
      key: string,
      options?: Readonly<{ container?: string }>
    ): Promise<void>;
  };
  readonly state: {
    /** 实例状态(作者声明的 `state.schema` 形状);`T` 由作者给出,宿主不校验泛型。 */
    get<T = unknown>(): T;
    /** @experimental 目前只有宿主自带模板在用(18a 稳定级别)。 */
    snapshot(): Promise<Omit<WidgetStateReceipt, 'changed'>>;
    set(
      value: unknown,
      options?: WidgetStateWriteOptions
    ): Promise<WidgetStateReceipt>;
    patch(
      value: Record<string, unknown>,
      options?: WidgetStateWriteOptions
    ): Promise<WidgetStateReceipt>;
    subscribe(listener: (value: unknown) => void): () => void;
  };
  readonly capability: {
    invoke<T = unknown>(
      namespace: string,
      method: string,
      params?: Record<string, unknown>
    ): Promise<T>;
    subscribe<T = unknown>(
      namespace: string,
      method: string,
      params: Record<string, unknown>,
      listener: (value: T) => void
    ): Promise<() => void>;
    /**
     * 这个命名空间(可选某方法)此刻能不能调:内置方法表恒可用,插件的要已安装、
     * 已启用且声明了该方法。同步读;装、卸、启停时宿主重新渲染组件,依赖别的
     * 插件的入口直接在渲染里据此显示或隐藏。可选依赖(`dependencies[].optional`)
     * 靠它降级,1.0 起 stable。
     */
    available(namespace: string, method?: string): boolean;
  };
  /**
   * 业务动作(17b):对一个或一批实体执行类型上声明的动作——与表格按钮、实体页、
   * AI 的 `entity_invoke`、自动化同一个执行口(`entity.invoke`),留同一种运行。
   * 小组件里的批量按钮用它:`xuwo.entity.run('favorite', selectedRefs)`。一个引用
   * 按单实体执行,多个(≤100)按选区执行、只留一次运行;动作的合同版本由宿主当场
   * 读(`entity.describe` 第一个引用)。选区回执里逐项给成败,部分失败不抛。
   * 呈现交互(切 tab、展开)不走这里,走 `xuwo.ui`。
   * 17b 新增;真实使用方是官方论文、小红书与资料库插件,17a 按「两个真实使用方」定为 stable。
   */
  readonly entity: {
    run(
      actionId: string,
      refs: readonly string[],
      params?: Record<string, unknown>
    ): Promise<EntityInvokeOutcome>;
  };
  /** @experimental */
  readonly local: {
    readonly available: boolean;
    invoke<I, O>(reference: WidgetLocalReference<I, O>, params: I): Promise<O>;
  };
  /**
   * 直接调用模型:应用里配好的模型连接(与本机模型网关同一份清单与转发),不拿
   * 令牌、不确认;每次调用记进应用用量账本,署名这个小组件的定义。只在桌面应用里
   * 可用(`available`);失败抛 `WidgetRuntimeError`,`code` 见 `WidgetAiErrorCode`。
   * @experimental
   */
  readonly ai: {
    readonly available: boolean;
    /** Append to the owning chat draft and focus it; never sends. Call from a user gesture. */
    compose(text: string): Promise<void>;
    models(): Promise<WidgetAiModelList>;
    /**
     * 弹应用的模型选择器。回选中的模型 id;选「默认」回空串;关掉没选回 null。
     * `kind: 'image'` 且没有能生图的连接时抛 `WIDGET_AI_NO_IMAGE_MODEL`。
     */
    pickModel(input: WidgetAiPickModelInput): Promise<string | null>;
    chat(
      request: WidgetAiChatRequest,
      options?: WidgetAiChatOptions
    ): Promise<WidgetAiChatResult>;
    /** 生一张图,存进本工作区的文件,返回引用;记账与署名同 `chat`。 */
    image(
      request: WidgetAiImageRequest,
      options?: WidgetAiImageOptions
    ): Promise<WidgetAiImageResult>;
  };
  readonly assets: {
    /** @experimental 真实使用方只有小红书一个插件(18a 稳定级别)。 */
    url(key: string): Promise<string | null>;
    /** @experimental */
    origin(): Promise<string>;
    /** @experimental */
    mount(url: string): Promise<string>;
    /** @experimental */
    stage(bytes: ArrayBuffer, mediaType: string): Promise<string>;
    /**
     * 随包资源(或独立副本已复制进工作区的那份)的可渲染地址。
     * @experimental
     */
    packageUrl(params: WidgetPackageAssetInput): Promise<WidgetPackageAsset>;
  };
  /**
   * 文件(D651,since 1.0;§7.3 起是文件属性的一项 `FileItem`,查看器挂载时就在实例
   * 状态 `FileViewerInput.file` 里)。字节在文件库里按 `key` 取;还在插件私有区的
   * 文件经它 `sourceUrl` 里的插件制品引用取;没有字节(只有来源地址)时报
   * `WIDGET_FILE_UNAVAILABLE`。
   * @experimental
   */
  readonly files: {
    /** 文件字节;`Blob.type` 是它的 MIME。 */
    read(file: FileItem): Promise<Blob>;
    /** 本机可取的同源地址(交给查看器自己的 iframe 等去取);只在桌面端可用。 */
    url(file: FileItem): Promise<string>;
    /**
     * 把一份文件(`<input type="file">` 选的图片等)存进工作区,回文件属性的一项
     * `FileItem`(`{ elementId, key, mediaType, name, size }`):原样放进文件属性的值
     * (经 `entity.update` 同一写口),也能直接交给 `read` / `url` 显示。按内容寻址,
     * 同一份字节只存一次。`name` 缺省取 `File.name`。
     */
    upload(
      file: Blob,
      options?: Readonly<{ name?: string }>
    ): Promise<FileItem>;
  };
  readonly ui: {
    /**
     * 声明这块实例的呈现交互(切 tab、展开、筛选……):人点按钮用 `invoke`,AI 用
     * `widget_control`,走同一个处理。重复调用整份替换,返回的函数撤销声明(在
     * effect 清理里调)。只做呈现——业务一律调实体动作或方法,不经这里(16c)。
     */
    expose(actions: readonly WidgetDeclaredAction[]): () => void;
    /**
     * 经宿主执行一个已声明的呈现交互(与 AI 的 `widget_control` 同一执行口),回执
     * `{ operationId, outcome, result, activity }`。一个实例同时只跑一个。
     */
    invoke(
      name: string,
      params?: unknown
    ): Promise<WidgetActionExecutionReceipt>;
    toast(text: string): Promise<void>;
    open(
      url: string,
      options?: { newTab?: boolean; reuse?: boolean; beside?: boolean }
    ): Promise<void>;
    /**
     * 在同一实例上打开详情(临时呈现,不存第二份配置)。
     * @experimental
     */
    openDetails(): Promise<Readonly<{ requested: boolean }>>;
    /** @experimental */
    closeDetails(): Promise<Readonly<{ requested: boolean }>>;
    /** 在 `rect` 处弹宿主菜单;回选中项的 `id`,关掉没选是 null。 */
    menu(input: WidgetMenuInput): Promise<string | null>;
  };
  readonly page: {
    readonly current: WidgetPageIdentity | null;
    /**
     * 这块小组件此刻显示的实体:规范引用串 `<authority>:<kind>:<工作区>/<id>[@version]`
     * (实体页、实体样式、对象页);普通文档里没有实体时为 null。样式小组件认「我在
     * 显示哪个实体」只走这一条,按前缀认类型(如 `entityRef?.startsWith('todo:item:')`)。
     */
    readonly entityRef: string | null;
    /**
     * 宿主内部的页面对象上下文(对象形引用与定位)。
     * @experimental 用 `entityRef`。
     */
    readonly context: ObjectContext | undefined;
    /** @experimental */
    readonly fragment: string;
    /** @experimental */
    readonly href: string;
    subscribe(listener: () => void): () => void;
    /** @experimental */
    properties(): Promise<readonly WidgetPageProperty[]>;
    /**
     * 当前页面(或实体页)里的附件与图片。
     * @experimental
     */
    assets(): Promise<readonly ContentAsset[]>;
  };
  readonly theme: WidgetTheme;
  readonly viewport: Readonly<{ width: number; height: number }>;
  readonly visible: boolean;
  readonly contentSizing: 'natural' | 'bounded';
  readonly readonly: boolean;
  readonly locale: string;
}
export type WidgetProps = Readonly<{ xuwo: WidgetClient }>;

/**
 * `WidgetClient` 里标了 `@experimental` 的成员(点号路径;对象成员整组算,如
 * `files` 覆盖 `files.read`)。文档注释是作者在编辑器里看到的标记,这张表是同一份
 * 标记的运行时读法:作者参考表(`WIDGET_SDK_REFERENCE`)的稳定级别从这里读。
 */
export const WIDGET_CLIENT_EXPERIMENTAL_MEMBERS: readonly string[] = [
  'expanded',
  'reducedMotion',
  'content.blocks',
  'content.replace',
  'content.arrange',
  'content.embed',
  'switcher',
  'state.snapshot',
  'local',
  'ai',
  'assets.url',
  'assets.origin',
  'assets.mount',
  'assets.stage',
  'assets.packageUrl',
  'files',
  'ui.openDetails',
  'ui.closeDetails',
  'page.context',
  'page.fragment',
  'page.href',
  'page.properties',
  'page.assets',
];

/** 客户端成员(点号路径)的稳定级别;成员本身或它所在的对象标了 experimental 就是 experimental。 */
export function widgetClientMemberStability(
  path: string
): 'stable' | 'experimental' {
  const parts = path.split('.');
  for (let length = 1; length <= parts.length; length += 1)
    if (
      WIDGET_CLIENT_EXPERIMENTAL_MEMBERS.includes(
        parts.slice(0, length).join('.')
      )
    )
      return 'experimental';
  return 'stable';
}

/** A compiler marker, not an evaluator. A missed transform must fail immediately. */
export function local<I, O>(
  _handler: (input: I, context: WidgetLocalContext) => O | Promise<O>
): WidgetLocalReference<I, Awaited<O>> {
  throw new Error(
    'WIDGET_LOCAL_NOT_COMPILED: local() must be a top-level const declaration.'
  );
}

export class WidgetRuntimeError extends Error {
  constructor(
    readonly code: string,
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'WidgetRuntimeError';
  }
}

/** Electron rejects lose custom Error fields, so execution outcomes cross IPC as data. */
export type WidgetCallReceipt =
  | Readonly<{ ok: true; value: unknown }>
  | Readonly<{ ok: false; error: Readonly<{ code: string; message: string }> }>;

export function unwrapWidgetCallReceipt(receipt: WidgetCallReceipt): unknown {
  if (receipt.ok) return receipt.value;
  throw new WidgetRuntimeError(receipt.error.code, receipt.error.message);
}

/** JSON data only; do not silently coerce NaN, class instances, functions or cycles. */
export function assertWidgetLocalValue(value: unknown): void {
  const active = new Set<object>();
  let budget = WIDGET_LOCAL_MAX_BYTES;
  const visit = (item: unknown, depth: number): void => {
    if (depth > 32 || --budget < 0)
      throw new WidgetRuntimeError(
        'WIDGET_LOCAL_VALUE_TOO_LARGE',
        'Local data exceeds the size or depth limit. Use a resource reference for large data.'
      );
    if (item === null || typeof item === 'boolean') return;
    if (typeof item === 'number' && Number.isFinite(item)) return;
    if (typeof item === 'string') {
      budget -= new TextEncoder().encode(item).byteLength;
      return;
    }
    if (typeof item !== 'object' || active.has(item))
      throw new WidgetRuntimeError(
        'WIDGET_LOCAL_VALUE_INVALID',
        'Local functions exchange finite JSON values, not handles or cyclic objects.'
      );
    const proto = Object.getPrototypeOf(item);
    if (!Array.isArray(item) && proto !== Object.prototype && proto !== null)
      throw new WidgetRuntimeError(
        'WIDGET_LOCAL_VALUE_INVALID',
        'Convert class instances to plain JSON values.'
      );
    active.add(item);
    for (const [key, child] of Object.entries(item)) {
      budget -= key.length;
      visit(child, depth + 1);
    }
    active.delete(item);
  };
  visit(value, 0);
  if (
    budget < 0 ||
    new TextEncoder().encode(JSON.stringify(value)).byteLength >
      WIDGET_LOCAL_MAX_BYTES
  )
    throw new WidgetRuntimeError(
      'WIDGET_LOCAL_VALUE_TOO_LARGE',
      'Local data exceeds 1 MiB. Use a resource reference.'
    );
}
